import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { useParams } from 'react-router-dom';
import apiService from '../../services/apiService';
import CallFormModal from '../../components/call-management/CallFormModal';
import FollowUpFormModal from '../../components/call-management/FollowUpFormModal';
import CallDetailsModal from '../../components/call-management/CallDetailsModal';

const outcomeLabel = {
  interested: 'Interested',
  not_interested: 'Not Interested',
  call_later: 'Call Later',
  no_answer: 'No Answer'
};

const VendorWallet = () => {
  const { user } = useAuth();
  const { vendorId } = useParams();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [sortBy, setSortBy] = useState('latest');
  const [vendorDetails, setVendorDetails] = useState(null);
  const [vendorServices, setVendorServices] = useState({
    packagesCount: 0,
    hotelsCount: 0,
    vehiclesCount: 0
  });
  const [summary, setSummary] = useState({
    totalCredit: 0,
    totalDebit: 0,
    totalReplacement: 0,
    remainingBalance: 0,
    totalCreditCount: 0,
    totalDebitCount: 0,
    totalAcceptedCount: 0,
    totalPendingCount: 0,
    totalRejectedCount: 0,
    totalPenaltyCount: 0
  });
  const [crmLeads, setCrmLeads] = useState(null);
  const [crmLeadsLoading, setCrmLeadsLoading] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    type: '',
    description: '',
    expireDays: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [viewCallModalOpen, setViewCallModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [detailsFollowUps, setDetailsFollowUps] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [callSubmitting, setCallSubmitting] = useState(false);
  const [callForm, setCallForm] = useState({
    id: '',
    name: '',
    phone: '',
    email: '',
    businessName: '',
    source: 'vendor',
    callType: 'outgoing',
    callStatus: '',
    note: '',
    nextFollowDateTime: '',
    followUpStatus: 'pending',
    followUpOutcome: 'call_later'
  });
  const [followUpForm, setFollowUpForm] = useState({
    id: '',
    callId: '',
    status: 'pending',
    outcome: '',
    note: '',
    callStatus: 'callback',
    nextFollowDateTime: ''
  });

  const fetchWalletData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        vendorId: vendorId,
        type: typeFilter,
        search: searchKeyword,
        sortBy: sortBy
      };
      
      const response = await apiService.post('walletHistoryV2', params);
      if (response && response.data) {
        setTransactions(response.data);
        setTotalRecords(response.recordsTotal || 0);
        if (response.vendorDetails) setVendorDetails(response.vendorDetails);
        if (response.summary) setSummary(response.summary);
      }
    } catch (error) {
      toast.error('Failed to load wallet data');
      console.error('Wallet fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, vendorId, typeFilter, searchKeyword, sortBy]);

  const fetchVendorServices = useCallback(async () => {
    try {
      if (!vendorId) return;
      const res = await apiService.get(`vendorServiceCounts/${vendorId}`);
      if (res && res.data) {
        setVendorServices(res.data);
      }
    } catch (error) {
      console.error('Failed to load vendor services', error);
    }
  }, [vendorId]);

  const fetchCrmLeads = useCallback(async () => {
    try {
      if (!vendorId) return;
      setCrmLeadsLoading(true);
      const res = await apiService.get(`vendorActivity/${vendorId}`);
      if (res && res.status && res.data?.crm?.leads) {
        setCrmLeads(res.data.crm.leads);
      }
    } catch (error) {
      console.error('Failed to load CRM leads', error);
    } finally {
      setCrmLeadsLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    fetchVendorServices();
    fetchCrmLeads();
  }, [fetchVendorServices, fetchCrmLeads]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchWalletData();
    }, searchKeyword ? 500 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [fetchWalletData]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setCurrentPage(1);
  };

  const handleTypeFilter = (value) => {
    setTypeFilter(value);
    setCurrentPage(1);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setCurrentPage(1);
  };

  const openModal = (transaction) => {
    setSelectedTransaction(transaction);
    setShowModal(true);
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || !formData.type || !formData.description || formData.expireDays === '') {
      toast.error('Please fill all required fields');
      return;
    }

    if (parseFloat(formData.amount) > 500) {
      toast.error('Amount cannot exceed ₹500');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiService.put(`vendorBonus/${vendorId}`, {
        amount: parseFloat(formData.amount),
        type: formData.type,
        description: formData.description,
        expireDays: Number(formData.expireDays)
      });
      
      if (response.status) {
        toast.success('Wallet entry added successfully');
        setShowAddModal(false);
        setFormData({ amount: '', type: '', description: '', expireDays: '' });
        fetchWalletData();
      } else {
        toast.error(response.message || 'Failed to add wallet entry');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add wallet entry');
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const profileViewUrl = vendorDetails?.slug
    ? `https://www.travellinglark.com/agent/${vendorDetails.slug}`
    : null;
  const profileEditUrl = `/vendor/edit/${vendorId}`;
  const vendorJoinDate = vendorDetails?.createdAt || vendorDetails?.created_at || vendorDetails?.joinDate || vendorDetails?.joiningDate || null;

  const getSourceBadge = (source) => {
    const normalized = (source || 'other').toString().toLowerCase();
    if (normalized === 'google') return 'G';
    if (normalized === 'meta') return 'M';
    return 'O';
  };


  const getAiResultBadge = (airesult) => {
    const intent = airesult?.intent || 'LOW';
    const config = {
      HIGH: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
      MEDIUM: 'bg-amber-50 text-amber-700 border border-amber-100',
      LOW: 'bg-slate-100 text-slate-700 border border-slate-200'
    };

    return {
      label: intent,
      color: config[intent] || config.LOW
    };
  };

  const formatAiScore = (airesult) => {
    if (airesult?.finalScore !== undefined && airesult?.finalScore !== null) return airesult.finalScore;
    if (airesult?.score !== undefined && airesult?.score !== null) return airesult.score;
    return '--';
  };

  const formatDateTime = (value) => {
    if (!value) return '--';
    return new Date(value).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (value) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '--';
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const normalizePhone = (value) => String(value || '').replace(/\D/g, '');

  const isSamePhone = (left, right) => {
    const leftPhone = normalizePhone(left);
    const rightPhone = normalizePhone(right);
    if (!leftPhone || !rightPhone) return false;
    return leftPhone === rightPhone || leftPhone.endsWith(rightPhone) || rightPhone.endsWith(leftPhone);
  };

  const buildCallPayloadFromVendor = () => ({
    name: `${vendorDetails?.first_name || ''} ${vendorDetails?.last_name || ''}`.trim() || vendorDetails?.business_name || 'Vendor',
    phone: vendorDetails?.phone || '',
    email: vendorDetails?.email || '',
    businessName: vendorDetails?.business_name || '',
    source: 'vendor',
    callType: 'outgoing',
    callStatus: '',
    duration: undefined,
    note: ''
  });

  const findVendorCallByPhone = async (phone) => {
    const normalized = normalizePhone(phone);
    if (!normalized) return null;

    try {
      const searches = [phone, normalized].map((value) => String(value || '')).filter(Boolean);
      for (const search of searches) {
        const response = await apiService.post('call/list', { start: 0, length: 200, search: String(search) });
        const rows = Array.isArray(response?.data) ? response.data : [];
        const exactMatch = rows.find((row) => {
          const rowPhone = row?.contactPhone || row?.contactId?.phone || row?.rawData?.contactId?.phone;
          return isSamePhone(rowPhone, normalized);
        });
        if (exactMatch) {
          return exactMatch?.rawData || exactMatch;
        }
      }
    } catch (error) {
      return null;
    }

    return null;
  };

  const loadCallFollowUps = async (callId) => {
    const response = await apiService.post('call/followup/list', { start: 0, length: 200 });
    const rows = Array.isArray(response?.data) ? response.data : [];
    return rows.filter((row) => row?.callId?._id === callId);
  };

  const openCreateCallModal = async () => {
    try {
      const payload = buildCallPayloadFromVendor();
      if (!payload.phone) {
        toast.error('Vendor phone is required');
        return;
      }
      const existingCall = await findVendorCallByPhone(payload.phone);
      setCallForm({
        id: existingCall?._id || '',
        name: payload.name,
        phone: payload.phone,
        email: payload.email,
        businessName: payload.businessName,
        source: 'vendor',
        callType: existingCall?.callType || 'outgoing',
        callStatus: existingCall?.lastFollowUp?.callStatus || '',
        note: existingCall?.note || '',
        nextFollowDateTime: existingCall?.lastFollowUp?.nextFollowDateTime || '',
        followUpStatus: existingCall?.lastFollowUp?.status || 'pending',
        followUpOutcome: existingCall?.lastFollowUp?.outcome || ''
      });
      setCallModalOpen(true);
    } catch (error) {
      toast.error('Failed to load call data');
    }
  };

  const saveCall = async (e) => {
    e.preventDefault();
    try {
      setCallSubmitting(true);
      const isCreate = !callForm.id;
      if (isCreate && callForm.followUpOutcome === 'callback' && !callForm.nextFollowDateTime) {
        toast.error('Next follow-up date and time is required for callback outcome');
        return;
      }

      const payload = {
        name: callForm.name,
        phone: callForm.phone,
        email: callForm.email,
        businessName: callForm.businessName,
        source: 'vendor',
        callType: callForm.callType,
        note: callForm.note
      };

      if (isCreate) {
        payload.nextFollowDateTime = callForm.followUpOutcome === 'callback' ? callForm.nextFollowDateTime : undefined;
        payload.outcome = callForm.followUpOutcome || undefined;
        payload.callStatus = callForm.callStatus || undefined;
      }

      if (callForm.id) {
        await apiService.put(`call/update/${callForm.id}`, payload);
        toast.success('Call updated successfully');
      } else {
        await apiService.post('call/create', payload);
        toast.success('Call created successfully');
      }
      setCallModalOpen(false);
      setCallForm({
        id: '',
        name: '',
        phone: '',
        email: '',
        businessName: '',
        source: 'vendor',
        callType: 'outgoing',
        callStatus: '',
        note: '',
        nextFollowDateTime: '',
        followUpStatus: 'pending',
        followUpOutcome: ''
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save call');
    } finally {
      setCallSubmitting(false);
    }
  };

  const openFollowUpModal = async () => {
    try {
      const existingCall = await findVendorCallByPhone(vendorDetails?.phone);
      if (!existingCall?._id) {
        toast.error('Create call first for this vendor');
        return;
      }
      setFollowUpForm({
        id: '',
        callId: existingCall._id,
        status: 'pending',
        outcome: '',
        note: '',
        callStatus: 'callback',
        nextFollowDateTime: ''
      });
      setFollowUpModalOpen(true);
    } catch (error) {
      toast.error('Failed to open follow-up');
    }
  };

  const saveFollowUp = async (e) => {
    e.preventDefault();
    if (!followUpForm.callId) {
      toast.error('Call is required');
      return;
    }
    if (followUpForm.outcome === 'callback' && !followUpForm.nextFollowDateTime) {
      toast.error('Next follow-up date and time is required for callback outcome');
      return;
    }
    try {
      if (followUpForm.id) {
        await apiService.put(`call/followup/update/${followUpForm.id}`, followUpForm);
        toast.success('Follow-up updated successfully');
      } else {
        await apiService.post('call/followup/create', followUpForm);
        toast.success('Follow-up created successfully');
      }
      setFollowUpModalOpen(false);
      setFollowUpForm({ id: '', callId: '', status: 'pending', outcome: '', note: '', callStatus: 'callback', nextFollowDateTime: '' });
      if (selectedCall?._id) {
        const updated = await loadCallFollowUps(selectedCall._id);
        setDetailsFollowUps(updated);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save follow-up');
    }
  };

  const openEditFollowUpForVendor = (followUp) => {
    setFollowUpForm({
      id: followUp?._id || '',
      callId: followUp?.callId?._id || followUp?.callId || selectedCall?._id || '',
      status: followUp?.status || 'pending',
      outcome: followUp?.outcome || '',
      note: followUp?.note || '',
      callStatus: followUp?.callStatus || '',
      nextFollowDateTime: followUp?.nextFollowDateTime || followUp?.followUpDateTime || ''
    });
    setFollowUpModalOpen(true);
  };

  const deleteFollowUpForVendor = async (followUp) => {
    if (!followUp?._id) return;
    const confirmed = window.confirm('Are you sure you want to delete this follow-up?');
    if (!confirmed) return;
    try {
      await apiService.delete(`call/followup/delete/${followUp._id}`);
      toast.success('Follow-up deleted successfully');
      if (selectedCall?._id) {
        const updated = await loadCallFollowUps(selectedCall._id);
        setDetailsFollowUps(updated);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete follow-up');
    }
  };

  const openViewCallModal = async () => {
    try {
      setDetailsLoading(true);
      const existingCall = await findVendorCallByPhone(vendorDetails?.phone);
      if (!existingCall?._id) {
        toast.error('No call found for this vendor');
        setDetailsLoading(false);
        return;
      }
      setSelectedCall(existingCall);
      setViewCallModalOpen(true);
      const followUps = await loadCallFollowUps(existingCall._id);
      setDetailsFollowUps(followUps);
    } catch (error) {
      toast.error('Failed to load call details');
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-0">
        {/* Breadcrumb */}
        <nav className="flex mb-0" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 text-xs">
            <li className="inline-flex items-center">
              <a href="/dashboard" className="text-gray-600 hover:text-[#009ef7]">Home</a>
            </li>
            <li>
              <div className="flex items-center">
                <span className="mx-1 text-gray-400">/</span>
                <a href="/caller-vendors" className="text-gray-600 hover:text-[#009ef7]">Vendors</a>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <span className="mx-1 text-gray-400">/</span>
                <span className="text-gray-500">Wallet</span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Vendor Details */}
        {vendorDetails && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-2 overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-[#009ef7] to-cyan-400"></div>
            <div className="p-4">
              {/* Row 1: Avatar + Name + Info chips */}
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                  {vendorDetails.image ? (
                    <img src={`${process.env.REACT_APP_MEDIA_URL}${vendorDetails.image}`} alt="vendor" className="w-full h-full object-cover" />
                  ) : (
                    <i className="bi bi-person-fill text-slate-300 text-2xl"></i>
                  )}
                </div>
                {/* Name + business + chips */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-bold text-slate-900">{vendorDetails.first_name} {vendorDetails.last_name}</span>
                    {vendorDetails.business_name && (
                      <span className="px-2 py-0.5 bg-[#009ef7]/10 text-[#009ef7] border border-[#009ef7]/20 rounded text-[11px] font-semibold">{vendorDetails.business_name}</span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                      vendorDetails.autoBuy
                        ? 'bg-emerald-500 text-white border-emerald-500'
                        : 'bg-slate-100 text-slate-400 border-slate-200'
                    }`}>
                      <i className={`bi ${vendorDetails.autoBuy ? 'bi-lightning-fill' : 'bi-lightning'} mr-0.5`}></i>
                      AutoBuy {vendorDetails.autoBuy ? 'ON' : 'OFF'}
                    </span>
                    {vendorDetails.phone && (
                      <a
                        href={`tel:${vendorDetails.phone}`}
                        className="inline-flex items-center gap-1 px-2.5 py-0.5 bg-emerald-500 text-white rounded text-[11px] font-semibold hover:bg-emerald-600 transition-colors no-underline"
                      >
                        <i className="bi bi-telephone-fill text-[10px]"></i>Call Now
                      </a>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {vendorDetails.phone && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
                        <i className="bi bi-telephone text-[#009ef7] text-[10px]"></i>{vendorDetails.phone}
                      </span>
                    )}
                    {vendorDetails.email && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
                        <i className="bi bi-envelope text-[#009ef7] text-[10px]"></i>{vendorDetails.email}
                      </span>
                    )}
                    {[vendorDetails.cityId?.name, vendorDetails.stateId?.name, vendorDetails.countryId?.name].filter(Boolean).length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
                        <i className="bi bi-geo-alt text-[#009ef7] text-[10px]"></i>
                        {[vendorDetails.cityId?.name, vendorDetails.stateId?.name, vendorDetails.countryId?.name].filter(Boolean).join(', ')}
                      </span>
                    )}
                    {vendorJoinDate && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600">
                        <i className="bi bi-calendar3 text-[#009ef7] text-[10px]"></i>{formatDateOnly(vendorJoinDate)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 2: Services */}
              <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2 border-t border-slate-100">
                <span className="text-[10px] text-slate-400 uppercase tracking-wide shrink-0"><i className="bi bi-box-seam mr-1"></i>Services:</span>
                <a href="/view-packages" className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-100 rounded text-[11px] font-medium hover:bg-purple-100 no-underline">
                  <i className="bi bi-briefcase text-[10px]"></i>Packages <span className="font-bold">{vendorServices.packagesCount}</span>
                </a>
                <a href="/hotel" className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[11px] font-medium hover:bg-blue-100 no-underline">
                  <i className="bi bi-building text-[10px]"></i>Hotels <span className="font-bold">{vendorServices.hotelsCount}</span>
                </a>
                <a href="/view-vehicles" className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-50 text-orange-700 border border-orange-100 rounded text-[11px] font-medium hover:bg-orange-100 no-underline">
                  <i className="bi bi-car-front text-[10px]"></i>Vehicles <span className="font-bold">{vendorServices.vehiclesCount}</span>
                </a>
              </div>

              {/* Row 3: Location covers */}
              {vendorDetails.locationCover?.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2 border-t border-slate-100">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide shrink-0"><i className="bi bi-map mr-1"></i>Covers:</span>
                  {vendorDetails.locationCover.map((loc, idx) => (
                    <span key={loc._id || idx} className="px-2 py-0.5 bg-[#009ef7]/10 text-[#009ef7] border border-[#009ef7]/20 rounded text-[11px] font-medium">
                      {loc.name || loc}
                    </span>
                  ))}
                </div>
              )}

              {/* Row 4: Interests */}
              {(vendorDetails.interests?.domestic?.stateIds?.length > 0 || vendorDetails.interests?.international?.length > 0) && (
                <div className="flex items-center gap-1.5 flex-wrap mt-2 pt-2 border-t border-slate-100">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide shrink-0"><i className="bi bi-heart mr-1"></i>Interests:</span>
                  {vendorDetails.interests?.domestic?.stateIds?.map((s, idx) => (
                    <span key={s._id || idx} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-100 rounded text-[11px] font-medium">
                      {s.name || s}
                    </span>
                  ))}
                  {vendorDetails.interests?.international?.map((item, idx) => (
                    <span key={item.countryId?._id || idx} className="px-2 py-0.5 bg-violet-50 text-violet-700 border border-violet-100 rounded text-[11px] font-medium">
                      {item.countryId?.name || item.countryId}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {/* CRM Leads */}
        {(crmLeads && crmLeads.total > 0) && (
          <div className="bg-white rounded-lg shadow-sm border mb-2 px-4 py-2">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 shrink-0">
                <i className="bi bi-people text-[#009ef7] text-xs"></i>
                <span className="text-xs font-semibold text-slate-600">CRM Leads</span>
                <span className="text-xs font-bold text-slate-800">{crmLeads.total}</span>
                <span className="text-[10px] text-slate-400">total</span>
                <span className="mx-1 text-slate-200">|</span>
                <span className="text-xs font-bold text-[#009ef7]">{crmLeads.thisMonth}</span>
                <span className="text-[10px] text-slate-400">this month</span>
              </div>
              {Object.keys(crmLeads.byStatus || {}).length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide">Status:</span>
                  {Object.entries(crmLeads.byStatus).sort(([,a],[,b]) => b - a).map(([status, count]) => {
                    const statusColors = {
                      'New':            'bg-blue-50 text-blue-700 border-blue-100',
                      'Contacted':      'bg-cyan-50 text-cyan-700 border-cyan-100',
                      'Not Answered':   'bg-slate-100 text-slate-600 border-slate-200',
                      'Follow-Up':      'bg-amber-50 text-amber-700 border-amber-100',
                      'In Discussion':  'bg-violet-50 text-violet-700 border-violet-100',
                      'Itinerary Sent': 'bg-indigo-50 text-indigo-700 border-indigo-100',
                      'Lost':           'bg-red-50 text-red-700 border-red-100',
                      'Confirmed':      'bg-emerald-50 text-emerald-700 border-emerald-100',
                      'Pending-Docs':   'bg-orange-50 text-orange-700 border-orange-100',
                      'Completed':      'bg-green-50 text-green-700 border-green-100',
                    };
                    const cls = statusColors[status] || 'bg-slate-100 text-slate-600 border-slate-200';
                    return (
                      <span key={status} className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded text-[11px] font-medium ${cls}`}>
                        {status} <span className="font-bold">{count}</span>
                      </span>
                    );
                  })}
                </div>
              )}
              {Object.keys(crmLeads.bySource || {}).length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide">Source:</span>
                  {Object.entries(crmLeads.bySource).sort(([,a],[,b]) => b - a).map(([src, count]) => (
                    <span key={src} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#009ef7]/8 rounded text-[11px] text-[#009ef7] font-medium capitalize">
                      {src} <span className="font-bold">{count}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Wallet Overview */}
        <div className="bg-white rounded-lg shadow-sm border mb-6 p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h4 className="text-lg font-bold flex items-center">
              <i className="bi bi-wallet mr-2 text-[#009ef7]"></i>Wallet Overview
            </h4>
            <div className="flex flex-wrap gap-2">
              <a
                href={profileViewUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  if (!profileViewUrl) {
                    e.preventDefault();
                    toast.error('Vendor profile URL is not available');
                  }
                }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md no-underline ${
                  profileViewUrl
                    ? 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <i className="bi bi-building mr-1"></i>View Profile
              </a>
              <a
                href={profileEditUrl}
                className="px-3 py-1.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-md hover:bg-blue-200 no-underline"
              >
                <i className="bi bi-pencil-square mr-1"></i>Edit Profile
              </a>
              <a
                href={`/vendor/activity/${vendorId}`}
                className="px-3 py-1.5 text-xs font-semibold bg-violet-100 text-violet-800 rounded-md hover:bg-violet-200 no-underline"
              >
                <i className="bi bi-activity mr-1"></i>Activity
              </a>
              <button
                type="button"
                onClick={openCreateCallModal}
                className="px-3 py-1.5 text-xs font-semibold bg-indigo-100 text-indigo-800 rounded-md hover:bg-indigo-200"
              >
                <i className="bi bi-telephone-plus mr-1"></i>Create Call
              </button>
              <button
                type="button"
                onClick={openFollowUpModal}
                className="px-3 py-1.5 text-xs font-semibold bg-amber-100 text-amber-800 rounded-md hover:bg-amber-200"
              >
                <i className="bi bi-calendar-check mr-1"></i>Follow-up
              </button>
              <button
                type="button"
                onClick={openViewCallModal}
                className="px-3 py-1.5 text-xs font-semibold bg-teal-100 text-teal-800 rounded-md hover:bg-teal-200"
              >
                <i className="bi bi-eye mr-1"></i>View
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <div className="text-green-600 text-2xl mb-2"><i className="bi bi-plus-circle"></i></div>
              <h6 className="text-green-600 font-semibold mb-1">Credit</h6>
              <div className="text-xl font-bold">₹{summary.totalCredit}</div>
              <small className="text-gray-500">Count: {summary.totalCreditCount}</small>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <div className="text-red-600 text-2xl mb-2"><i className="bi bi-dash-circle"></i></div>
              <h6 className="text-red-600 font-semibold mb-1">Debit</h6>
              <div className="text-xl font-bold">₹{summary.totalDebit}</div>
              <small className="text-gray-500">Count: {summary.totalDebitCount}</small>
            </div>
            <div className="bg-yellow-50 rounded-lg p-2">
              <div className="flex items-center mb-2">
                <div className="text-yellow-600 text-xl mr-2"><i className="bi bi-arrow-repeat"></i></div>
                <div>
                  <h6 className="text-yellow-600 font-semibold text-sm">Replacement</h6>
                  <div className="text-lg font-bold">₹{summary.totalReplacement}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-1 text-xs">
                <div className="bg-green-100 rounded p-1 text-center"><div className="font-bold text-green-700">{summary.totalAcceptedCount}</div><div className="text-gray-600">Accepted</div></div>
                <div className="bg-yellow-100 rounded p-1 text-center"><div className="font-bold text-yellow-700">{summary.totalPendingCount}</div><div className="text-gray-600">Pending</div></div>
                <div className="bg-red-100 rounded p-1 text-center"><div className="font-bold text-red-700">{summary.totalRejectedCount}</div><div className="text-gray-600">Rejected</div></div>
                <div className="bg-gray-100 rounded p-1 text-center"><div className="font-bold text-gray-700">{summary.totalPenaltyCount}</div><div className="text-gray-600">Penalty</div></div>
              </div>
            </div>
            <div className="bg-[#009ef7]/10 rounded-lg p-4 text-center">
              <div className="text-[#009ef7] text-2xl mb-2"><i className="bi bi-coin"></i></div>
              <h6 className="text-[#009ef7] font-semibold mb-1">Balance</h6>
              <div className="text-xl font-bold">₹{summary.remainingBalance}</div>
              <small className="text-gray-500">Available Now</small>
              {(() => {
                const lastCredit = transactions
                  .filter(t => t.type === 'credit' && t.expireDate)
                  .sort((a, b) => new Date(b.expireDate) - new Date(a.expireDate))[0];
                if (!lastCredit) return null;
                const isExpired = new Date(lastCredit.expireDate) < new Date();
                return (
                  <div className={`mt-2 px-2 py-1 rounded text-[10px] font-semibold border ${
                    isExpired
                      ? 'bg-red-50 text-red-600 border-red-200'
                      : 'bg-amber-50 text-amber-700 border-amber-200'
                  }`}>
                    <i className={`bi ${isExpired ? 'bi-x-circle' : 'bi-clock'} mr-1`}></i>
                    {isExpired ? 'Expired' : 'Expires'}: {formatDateOnly(lastCredit.expireDate)}
                  </div>
                );
              })()}
            </div>
            <div className="bg-cyan-50 rounded-lg p-4 text-center">
              <div className="text-cyan-600 text-2xl mb-2"><i className="bi bi-cart"></i></div>
              <h6 className="text-cyan-600 font-semibold mb-1">Leads</h6>
              <div className="text-xl font-bold">{summary.totalDebitCount}</div>
              <small className="text-gray-500">Purchased</small>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b">
            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="flex flex-wrap gap-3">
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchKeyword}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7] w-48"
                />
                <select
                  value={typeFilter}
                  onChange={(e) => handleTypeFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                >
                  <option value="All">All</option>
                  <option value="credit">Credit</option>
                  <option value="debit">Debit</option>
                  <option value="refund">Refund</option>
                </select>
                <select
                  value={sortBy}
                  onChange={(e) => handleSortChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                >
                  <option value="latest">Latest</option>
                  <option value="oldest">Oldest</option>
                </select>
              </div>
              {user?.role === 'admin' && (
                <button 
                  onClick={() => setShowAddModal(true)}
                  className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 text-sm"
                >
                  <i className="bi bi-plus mr-1"></i>Add Entry
                </button>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Traveler</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Query</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[220px]">Dates</th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#009ef7]"></div>
                      </div>
                    </td>
                  </tr>
                ) : transactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-gray-500">No transactions found</td>
                  </tr>
                ) : (
                  transactions.map((transaction, index) => (
                    <tr key={transaction._id} className="hover:bg-gray-50">
                      <td className="px-2 sm:px-4 py-3 text-sm">{((currentPage - 1) * recordsPerPage) + index + 1}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold mb-1">
                          {transaction.type === 'credit' ? (
                            <span className="text-green-600">+₹{transaction.amount}</span>
                          ) : (
                            <span className="text-red-600">-₹{transaction.amount}</span>
                          )}
                        </div>
                        {transaction.type === 'credit' ? (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">Credit</span>
                        ) : transaction.type === 'replacement' ? (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded">Replacement</span>
                        ) : (
                          <div>
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded">Debit</span>
                            {transaction.replacementRequestStatus && transaction.replacementRequest && (
                              <div className="text-xs mt-1">
                                {transaction.replacementRequest.verify === 'Accepted' && <span className="text-green-600">Refunded</span>}
                                {transaction.replacementRequest.verify === 'Rejected' && <span className="text-red-600">Rejected</span>}
                                {transaction.replacementRequest.verify === 'Penalty' && <span className="text-red-600">Penalty</span>}
                                {transaction.replacementRequest.verify === 'Pending' && <span className="text-yellow-600">Pending</span>}
                                {!transaction.replacementRequest.verify && <span className="text-yellow-600">Pending</span>}
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      <td className="px-2 sm:px-4 py-3 text-sm">{transaction.description?.substring(0, 30)}{transaction.description?.length > 30 ? '...' : ''}</td>
                      <td className="px-2 sm:px-4 py-3">
                        {transaction.queryId?.userData ? (
                          <div className="text-xs text-slate-700 min-w-[140px]">
                            <div className="font-semibold text-gray-800">
                              {transaction.queryId.userData.first_name} {transaction.queryId.userData.last_name}
                            </div>
                            <div className="text-gray-500 mt-0.5">{transaction.queryId.userData.phone || '--'}</div>
                            <div className="text-gray-400 truncate max-w-[160px]">{transaction.queryId.userData.email || '--'}</div>
                            <div className="flex gap-1 mt-1">
                              {transaction.queryId.userData.phoneVerified && (
                                <span className="px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-100 rounded text-[10px] font-medium">✓ Phone</span>
                              )}
                              {transaction.queryId.userData.emailVerified && (
                                <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-100 rounded text-[10px] font-medium">✓ Email</span>
                              )}
                            </div>
                          </div>
                        ) : '--'}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm">
                        {transaction.queryId?.id ? (
                          <div className="min-w-[220px] max-w-[300px] text-xs text-slate-700">
                            <div className="flex items-center justify-between gap-3">
                              <a
                                href={`https://www.travellinglark.com/travel-lead/details/${transaction.queryId.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#009ef7] font-semibold no-underline hover:underline"
                              >
                                Query #{transaction.queryId.id}
                              </a>
                              <div className="flex items-center gap-2">
                                <span
                                  className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#009ef7]/10 px-2 text-[10px] font-semibold text-[#009ef7]"
                                  title={`Source: ${(transaction.queryId?.source || 'other').toString().toLowerCase()}`}
                                >
                                  {getSourceBadge(transaction.queryId?.source)}
                                </span>
                                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.04em] ${getAiResultBadge(transaction.queryId?.airesult).color}`}>
                                  {getAiResultBadge(transaction.queryId?.airesult).label}
                                </span>
                              </div>
                            </div>

                            <div className="mt-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2">
                              <div className="flex items-start gap-2 text-[11px]">
                                <div className="flex flex-col items-center pt-0.5">
                                  <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                                  <span className="h-4 w-px bg-slate-200"></span>
                                  <span className="h-2 w-2 rounded-full bg-rose-500"></span>
                                </div>
                                <div className="min-w-0 flex-1 space-y-1.5">
                                  <div className="flex items-start gap-1.5">
                                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">From</span>
                                    <span className="break-words font-medium text-slate-700">{transaction.queryId?.leavingFrom?.name || 'N/A'}</span>
                                  </div>
                                  <div className="flex items-start gap-1.5">
                                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">To</span>
                                    <span className="break-words font-medium text-slate-700">{transaction.queryId?.goingTo?.name || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-semibold capitalize text-slate-600">
                                Call: {transaction.queryId?.callStatus || 'pending'}
                              </span>
                              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">
                                Score: {formatAiScore(transaction.queryId?.airesult)}
                              </span>
                            </div>
                          </div>
                        ) : '--'}
                      </td>
                      <td className="px-2 sm:px-4 py-3 text-sm">
                        <div>
                          <span className="text-[11px] text-gray-500">Purchased:</span>{' '}
                          <span>{formatDateOnly(transaction.createdAt)}</span>
                          <div className="text-xs text-gray-500">{new Date(transaction.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                        </div>
                        {transaction.type === 'credit' && (
                          <div className="mt-1">
                            <span className="text-[11px] text-gray-500">Expire:</span>{' '}
                            {transaction.expireDate ? (
                              <>
                                <span>{formatDateOnly(transaction.expireDate)}</span>
                                <div className="text-xs text-gray-500">{new Date(transaction.expireDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                              </>
                            ) : (
                              <span>--</span>
                            )}
                          </div>
                        )}
                        {transaction.type === 'debit' && (
                          <div className="mt-1">
                            <span className="text-[11px] text-gray-500">Query:</span>{' '}
                            {transaction.queryId?.createdAt ? (
                              <>
                                <span>{formatDateOnly(transaction.queryId.createdAt)}</span>
                                <div className="text-xs text-gray-500">{new Date(transaction.queryId.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                              </>
                            ) : (
                              <span>--</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openModal(transaction)}
                          className="px-3 py-1 bg-[#009ef7]/10 text-blue-700 text-xs font-semibold rounded hover:bg-blue-200"
                        >
                          <i className="bi bi-eye mr-1"></i>View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm text-gray-700">Show</span>
                <select
                  value={recordsPerPage}
                  onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-xs sm:text-sm text-gray-700">entries</span>
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-center">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
                >
                  «
                </button>
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className="px-3 py-1 border rounded text-xs sm:text-sm"
                        style={{
                          backgroundColor: currentPage === page ? '#009ef7' : '#fff',
                          borderColor: currentPage === page ? '#009ef7' : '#d1d5db',
                          color: currentPage === page ? '#fff' : '#374151'
                        }}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2 text-gray-500">...</span>;
                  }
                  return null;
                })}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
                >
                  »
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold">Wallet Transaction Details</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="p-6">
              {selectedTransaction.userId && (
                <div className="border border-green-300 rounded-lg p-4 mb-4 bg-green-50">
                  <h6 className="font-semibold text-green-700 mb-3">Lead Buyer Information (Vendor)</h6>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-gray-600">Name:</span> <span className="font-semibold">{selectedTransaction.userId.first_name} {selectedTransaction.userId.last_name}</span></div>
                    <div><span className="text-gray-600">Business:</span> <span className="font-semibold">{selectedTransaction.userId.business_name || '--'}</span></div>
                    <div><span className="text-gray-600">Phone:</span> <span className="font-semibold">{selectedTransaction.userId.phone || '--'}</span></div>
                    <div><span className="text-gray-600">Email:</span> <span className="font-semibold">{selectedTransaction.userId.email || '--'}</span></div>
                  </div>
                </div>
              )}
              {selectedTransaction.queryId?.userData && (
                <div className="border border-blue-300 rounded-lg p-4 mb-4 bg-[#009ef7]/10">
                  <h6 className="font-semibold text-blue-700 mb-3">Lead Owner Information (Customer)</h6>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><span className="text-gray-600">Name:</span> <span className="font-semibold">{selectedTransaction.queryId.userData.first_name} {selectedTransaction.queryId.userData.last_name}</span></div>
                    <div><span className="text-gray-600">Phone:</span> <span className="font-semibold">{selectedTransaction.queryId.userData.phone || '--'}</span></div>
                    <div><span className="text-gray-600">Email:</span> <span className="font-semibold">{selectedTransaction.queryId.userData.email || '--'}</span></div>
                  </div>
                </div>
              )}
              <div className="space-y-3 text-sm">
                <div><span className="text-gray-600">Type:</span> <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${selectedTransaction.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{selectedTransaction.type}</span></div>
                <div><span className="text-gray-600">Amount:</span> <span className="font-bold ml-2">₹{selectedTransaction.amount}</span></div>
                <div><span className="text-gray-600">Description:</span> <span className="ml-2">{selectedTransaction.description || 'No description'}</span></div>
                <div><span className="text-gray-600">Created:</span> <span className="ml-2">{new Date(selectedTransaction.createdAt).toLocaleString('en-IN')}</span></div>
                {selectedTransaction.queryId?.id && (
                  <div className="rounded-lg border border-violet-100 bg-violet-50/70 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-gray-600">Lead Priority:</span>
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] ${getAiResultBadge(selectedTransaction.queryId?.airesult).color}`}>
                        {getAiResultBadge(selectedTransaction.queryId?.airesult).label}
                      </span>
                      <span className="text-xs text-slate-600">Score: {formatAiScore(selectedTransaction.queryId?.airesult)}</span>
                    </div>
                  </div>
                )}
              </div>
              {selectedTransaction.replacementRequestStatus && selectedTransaction.replacementRequest && (
                <div className="border-t pt-4 mt-4">
                  <h6 className="font-semibold text-yellow-700 mb-3">Replacement Request Information</h6>
                  <div className="space-y-2 text-sm">
                    <div><span className="text-gray-600">Status:</span> 
                      <span className={`ml-2 px-2 py-1 rounded text-xs font-semibold ${
                        selectedTransaction.replacementRequest.verify === 'Accepted' ? 'bg-green-100 text-green-800' :
                        selectedTransaction.replacementRequest.verify === 'Rejected' ? 'bg-red-100 text-red-800' :
                        selectedTransaction.replacementRequest.verify === 'Penalty' ? 'bg-gray-800 text-white' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>{selectedTransaction.replacementRequest.verify || 'Pending'}</span>
                    </div>
                    <div><span className="text-gray-600">Refund Amount:</span> <span className="font-bold ml-2">₹{selectedTransaction.replacementRequest.amount || 0}</span></div>
                    <div><span className="text-gray-600">Reason:</span> <span className="ml-2">{selectedTransaction.replacementRequest.reason || 'Not specified'}</span></div>
                    <div><span className="text-gray-600">Description:</span> <span className="ml-2">{selectedTransaction.replacementRequest.description || 'No description'}</span></div>
                    {selectedTransaction.replacementRequestedAt && (
                      <div><span className="text-gray-600">Requested:</span> <span className="ml-2">{new Date(selectedTransaction.replacementRequestedAt).toLocaleString('en-IN')}</span></div>
                    )}
                    {selectedTransaction.replacementRequest.requestVerifiedAt && (
                      <div><span className="text-gray-600">Verified:</span> <span className="ml-2">{new Date(selectedTransaction.replacementRequest.requestVerifiedAt).toLocaleString('en-IN')}</span></div>
                    )}
                    {selectedTransaction.replacementRequest.rejectMessage && (
                      <div className="bg-red-50 border border-red-200 rounded p-2 mt-2">
                        <span className="text-red-700 font-semibold">Reject Message:</span>
                        <div className="text-red-600 mt-1">{selectedTransaction.replacementRequest.rejectMessage}</div>
                      </div>
                    )}
                    {(selectedTransaction.proof || selectedTransaction.replacementRequest.proof) && (
                      <div className="mt-3">
                        <span className="text-gray-600 block mb-2">Proof Images:</span>
                        <div className="flex flex-wrap gap-2">
                          {(selectedTransaction.proof || selectedTransaction.replacementRequest.proof).map((img, idx) => (
                            <a key={idx} href={`${process.env.REACT_APP_MEDIA_URL}${img}`} target="_blank" rel="noopener noreferrer">
                              <img src={`${process.env.REACT_APP_MEDIA_URL}${img}`} alt={`Proof ${idx + 1}`} className="w-20 h-20 object-cover rounded border" />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                    {(selectedTransaction.audio || selectedTransaction.replacementRequest.audio) && (
                      <div className="mt-3">
                        <span className="text-gray-600 block mb-2">Audio Files:</span>
                        {(selectedTransaction.audio || selectedTransaction.replacementRequest.audio).map((audio, idx) => (
                          <audio key={idx} controls className="w-full max-w-sm mb-2">
                            <source src={`${process.env.REACT_APP_MEDIA_URL}${audio}`} type="audio/mpeg" />
                          </audio>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t flex justify-end">
              <button onClick={() => setShowModal(false)} className="px-2 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Entry Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-lg max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-bold">Add Wallet Entry</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <form onSubmit={handleAddEntry} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="Enter amount"
                    min="1"
                    max="500"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum amount allowed: ₹500</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type <span className="text-red-500">*</span></label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({...formData, type: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  >
                    <option value="">Select Type</option>
                    <option value="credit">Credit</option>
                    <option value="debit">Debit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expire Days <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={formData.expireDays}
                    onChange={(e) => setFormData({...formData, expireDays: e.target.value})}
                    placeholder="Enter expire days"
                    min="1"
                    step="1"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Enter description"
                    rows="3"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-2 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded hover:bg-[#009ef7]/90 disabled:opacity-50"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <CallFormModal
        open={callModalOpen}
        form={callForm}
        setForm={setCallForm}
        onClose={() => setCallModalOpen(false)}
        onSubmit={saveCall}
        submitting={callSubmitting}
      />

      <FollowUpFormModal
        open={followUpModalOpen}
        form={followUpForm}
        setForm={setFollowUpForm}
        onClose={() => setFollowUpModalOpen(false)}
        onSubmit={saveFollowUp}
      />

      <CallDetailsModal
        open={viewCallModalOpen}
        call={selectedCall}
        followUps={detailsFollowUps}
        loading={detailsLoading}
        onClose={() => setViewCallModalOpen(false)}
        onAddFollowUp={() => {
          setFollowUpForm({ id: '', callId: selectedCall?._id, status: 'pending', outcome: '', note: '', callStatus: 'callback', nextFollowDateTime: '' });
          setFollowUpModalOpen(true);
        }}
        formatDateTime={formatDateTime}
        outcomeLabel={outcomeLabel}
        onEditFollowUp={openEditFollowUpForVendor}
        onDeleteFollowUp={deleteFollowUpForVendor}
      />
    </div>
  );

};
export default VendorWallet;














